import type * as React from 'react';

import * as api from '@gitbook/api-client';
import {
    type AlertAction,
    Badge,
    type BadgeProps,
    Button,
    DialogTrigger,
    Link,
} from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { useContactSupport } from '../../../../state';

export type AdsReviewStatus = Extract<
    api.SiteAds,
    { status: 'pending' } | { status: 'in-review' } | { status: 'rejected' }
>;

export function isSiteAdsInReviewStatus(siteAds: api.SiteAds): siteAds is AdsReviewStatus {
    return (
        siteAds.status !== api.SiteAdsStatus.Live && siteAds.status !== api.SiteAdsStatus.Disabled
    );
}

/**
 * Returns the props to use for the Ads status alert and indicator for the different review statuses.
 */
export function useAdsReviewStatus(props: { site: api.Site; ads: AdsReviewStatus }): {
    badge: BadgeProps;
    details: React.ReactNode;
    action?: AlertAction;
} {
    const { site, ads } = props;
    const contactSupport = useContactSupport();

    switch (ads.status) {
        case 'pending':
            return {
                badge: { label: 'Pending', kind: 'secondary' },
                details: ads.submittable ? (
                    <>
                        You can now submit your site for an ad review to check that it meets the
                        requirements.
                    </>
                ) : (
                    <>
                        Before submitting your site for ad review, please ensure it meets the
                        requirements.
                    </>
                ),
                action: ads.submittable ? { label: 'Request review' } : { label: 'Ads checklist' },
            };
        case 'in-review': {
            const siteCreationDate = new Date(site.createdAt);
            siteCreationDate.setDate(siteCreationDate.getDate() + 7);
            const expectedReviewDate = siteCreationDate.toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
            });

            return {
                badge: { label: 'In review', kind: 'info' },
                details: (
                    <>
                        Your site is in ad review. You should have the results by{' '}
                        {expectedReviewDate}.
                    </>
                ),
            };
        }
        case 'rejected':
            return {
                badge: { label: 'Rejected', kind: 'danger' },
                details: (
                    <>
                        Your site didn't meet the criteria for displaying ads. Please
                        <>
                            {' '}
                            <Link
                                onPress={() => {
                                    contactSupport();
                                }}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                contact support
                            </Link>{' '}
                        </>
                        if you'd like to resubmit it.
                    </>
                ),
            };
        default:
            assertNever(ads);
    }
}

export function AdsAllStatusIndicator({
    site,
    ads,
}: {
    organizationId: string;
    site: api.Site;
    ads: api.SiteAds;
}) {
    switch (ads.status) {
        case 'live':
            return null;
        case 'pending':
        case 'in-review':
        case 'rejected':
            return <AdsReviewStatusIndicator site={site} ads={ads} />;
        case 'disabled':
            return null;
        default:
            assertNever(ads);
    }
}

function AdsReviewStatusIndicator(props: { site: api.Site; ads: AdsReviewStatus }) {
    const reviewStatusProps = useAdsReviewStatus(props);

    return (
        <AdsStatusIndicator
            badge={
                props.ads.status !== api.SiteAdsStatus.Pending ? reviewStatusProps.badge : undefined
            }
            action={
                reviewStatusProps.action ? (
                    <DialogTrigger>
                        <Button label={reviewStatusProps.action.label} />
                    </DialogTrigger>
                ) : undefined
            }
        >
            {reviewStatusProps.details}
        </AdsStatusIndicator>
    );
}

export function AdsStatusIndicator({
    badge,
    children,
    action,
}: {
    badge?: BadgeProps;
    children: React.ReactNode;
    action?: React.ReactNode;
}) {
    return (
        <div className="flex items-center gap-3">
            {badge ? <Badge {...badge} /> : null}
            <div className="flex-1 text-secondary text-ui-base">{children}</div>
            {action ? <div className="flex items-center gap-2">{action}</div> : null}
        </div>
    );
}
